import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Plus, Trash, ArrowsClockwise, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const inputCls = "px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-neon-cyan/60";

export default function AdminAccessList() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/allowed-admins");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (ev) => {
    ev.preventDefault();
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post("/admin/allowed-admins", {
        email: newEmail.trim(),
        label: newLabel.trim(),
      });
      setItems((prev) => [...prev, data]);
      setNewEmail("");
      setNewLabel("");
      toast.success("Admin access granted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id, email) => {
    if (!window.confirm(`Revoke admin access for ${email}?`)) return;
    try {
      await api.delete(`/admin/allowed-admins/${id}`);
      setItems((prev) => prev.filter((e) => e.id !== id));
      toast.success("Admin access revoked");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const currentEmail = (user?.email || "").toLowerCase();

  return (
    <section data-testid="admin-access-list">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs font-bold tracking-[0.3em] text-neon-cyan uppercase mb-2 inline-flex items-center gap-2">
            <ShieldCheck size={14} weight="fill" /> Admin Access
          </p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tighter">Who can run the Shoow.</h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Only these Google emails are promoted to <span className="text-neon-cyan font-mono">admin</span> when they sign in. Everyone else stays a public listener. You can't remove yourself, and at least one email must remain.
          </p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/50 text-xs" data-testid="admin-access-reload">
          <ArrowsClockwise size={14} /> Reload
        </button>
      </div>

      {/* Add form */}
      <form onSubmit={add} className="rounded-xl border border-ink-600 bg-ink-900/40 p-4 mb-4 grid grid-cols-1 sm:grid-cols-12 gap-3 items-end" data-testid="admin-access-add-form">
        <div className="sm:col-span-5">
          <label className="text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-400 block mb-1.5">Google email</label>
          <input type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="name@gmail.com" className={`${inputCls} w-full`} data-testid="admin-access-new-email" />
        </div>
        <div className="sm:col-span-5">
          <label className="text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-400 block mb-1.5">Label (optional)</label>
          <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Owner, Manager, etc." className={`${inputCls} w-full`} data-testid="admin-access-new-label" />
        </div>
        <div className="sm:col-span-2">
          <button type="submit" disabled={adding} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-neon-cyan text-black text-xs font-semibold hover:bg-neon-cyanHover disabled:opacity-50" data-testid="admin-access-add-btn">
            <Plus size={14} weight="bold" /> {adding ? "Adding..." : "Grant"}
          </button>
        </div>
      </form>

      {/* List */}
      <div className="rounded-xl border border-ink-600 bg-ink-900/40 overflow-hidden">
        {loading ? (
          <div className="px-4 py-6 text-zinc-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-4 py-10 text-center text-zinc-500 text-sm">No admins configured yet. Add one above.</div>
        ) : (
          <ul>
            {items.map((item) => {
              const isSelf = (item.email_lower || item.email.toLowerCase()) === currentEmail;
              return (
                <li key={item.id} className="border-b border-ink-700/60 last:border-0 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`admin-access-row-${item.id}`}>
                  <User size={16} className="text-neon-cyan" weight="fill" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white text-sm font-mono break-all">{item.email}</span>
                      {isSelf && (
                        <span className="text-[10px] font-mono uppercase tracking-[0.2em] px-1.5 py-0.5 rounded bg-ink-700 text-neon-cyan font-bold" data-testid={`admin-access-${item.id}-self-badge`}>
                          you
                        </span>
                      )}
                    </div>
                    {item.label && <p className="text-xs text-zinc-500 mt-0.5">{item.label}</p>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => remove(item.id, item.email)}
                      disabled={isSelf || items.length === 1}
                      className="p-1.5 rounded border border-ink-600 text-zinc-400 hover:text-neon-magenta hover:border-neon-magenta/50 disabled:opacity-30 disabled:cursor-not-allowed"
                      title={isSelf ? "You can't remove yourself" : items.length === 1 ? "At least one admin required" : "Revoke access"}
                      data-testid={`admin-access-${item.id}-delete`}
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
